import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Chip, Stack, Typography } from '@mui/material'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded'
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded'

const isDocsHost = () =>
  window.location.hostname === `docs.${process.env.ROOT_DOMAIN}` ||
  window.location.hostname.startsWith('docs.')

const MODULES = [
  {
    title: 'Getting Started',
    color: '#239AA4',
    to: '/getting-started/welcome',
    summary:
      'Start with the AmbuNet overview, home screen, permissions, and access model.',
  },
  {
    title: 'For Crew & Staff',
    color: '#5B8DEF',
    to: '/crew/your-shifts',
    summary:
      'Your shifts, availability, pay, training, daily crew tools, and what you are asked to read and acknowledge.',
  },
  {
    title: 'Employees & HR',
    color: '#F28B1F',
    to: '/employees-hr/people-and-shifts',
    summary:
      'Manage staff records, onboarding, scheduling, payroll, availability, and workforce administration.',
  },
  {
    title: 'Event Planning',
    color: '#268EBB',
    to: '/event-planning/calendar-and-events',
    summary:
      'Plan events with proposals, shifts, documents, operational plans, risk assessments, and reviews.',
  },
  {
    title: 'Finance',
    color: '#CCA43B',
    to: '/finance/overview',
    summary:
      'Track invoices, credit notes, payments, quotes, unpaid balances, Xero exports, and finance KPIs.',
  },
  {
    title: 'Clinical Records',
    color: '#85276A',
    to: '/clinical-records/starting-and-finding',
    summary:
      'Create, review, share, and govern ePCRs, paper PRFs, attachments, and clinical feedback.',
  },
  {
    title: 'Clinical Governance',
    color: '#E32525',
    to: '/clinical-governance/incidents-and-safety-alerts',
    summary:
      'Track incidents, audits, medicines, KPIs, meetings, policies, SOPs, PGDs, and safety alerts.',
  },
  {
    title: 'Fleet & Equipment',
    color: '#00964C',
    to: '/fleet-equipment/vehicles-and-assets',
    summary:
      'Manage vehicles, assets, defects, checks, make ready, maintenance, and QR-code workflows.',
  },
  {
    title: 'Command & Control',
    color: '#64748B',
    to: '/command-control/overview',
    summary:
      'Coordinate live operations with tactical logs, control rooms, command logs, and resources.',
  },
  {
    title: 'Patient Transport',
    color: '#239AA4',
    to: '/patient-transport/patients',
    summary:
      'Handle patients, bookings, journeys, crews, hospitals, and patient transport control workflows.',
  },
]

export default function DocsLandingPage() {
  // On the docs.* subdomain, doc pages live at the root; inside the main
  // app they live under /docs. Match DocsSidebar's getDocHref convention.
  const base = isDocsHost() ? '' : '/docs'

  return (
    <Box sx={{ maxWidth: 1060 }}>
      <Stack spacing={1.5} sx={{ mb: 4 }}>
        <Chip
          icon={<AutoStoriesRoundedIcon />}
          label="Knowledgebase"
          size="small"
          sx={{
            alignSelf: 'flex-start',
            borderRadius: 1.5,
            fontWeight: 800,
            color: '#1E7F88',
            bgcolor: 'rgba(35,154,164,0.10)',
            '& .MuiChip-icon': { color: '#239AA4' },
          }}
        />

        <Typography
          component="h1"
          sx={{
            color: '#242F40',
            fontSize: { xs: 28, md: 38 },
            fontWeight: 700,
            lineHeight: 1.12,
            letterSpacing: 0,
          }}
        >
          Find the AmbuNet workflow you need.
        </Typography>

        <Typography
          sx={{
            color: '#64748B',
            fontSize: { xs: 14, md: 16 },
            lineHeight: 1.75,
            maxWidth: 720,
          }}
        >
          Browse practical setup guides, module walkthroughs, and reference
          articles for the operational tools your teams use every day.
        </Typography>
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            xl: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 1.5,
        }}
      >
        {MODULES.map(({ title, to, summary, color }) => (
          <Box
            key={title}
            component={RouterLink}
            to={`${base}${to}`}
            sx={{
              minHeight: 150,
              p: 2,
              borderRadius: 2,
              border: '1px solid rgba(15,23,42,0.08)',
              bgcolor: '#FFFFFF',
              textDecoration: 'none',
              color: 'inherit',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              transition:
                'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
              '&:hover': {
                borderColor: color,
                boxShadow: '0 16px 42px rgba(15,23,42,0.10)',
                transform: 'translateY(-2px)',
                '& .docs-card-arrow': {
                  transform: 'translateX(3px)',
                },
              },
            }}
          >
            <Box>
              <Box
                sx={{
                  width: 34,
                  height: 4,
                  borderRadius: 999,
                  bgcolor: color,
                  mb: 1.75,
                }}
              />

              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: '#242F40',
                  mb: 0.75,
                }}
              >
                {title}
              </Typography>

              <Typography
                sx={{
                  color: '#64748B',
                  fontSize: 12.5,
                  lineHeight: 1.6,
                }}
              >
                {summary}
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ mt: 2, color }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                Open module
              </Typography>
              <ArrowForwardRoundedIcon
                className="docs-card-arrow"
                sx={{ fontSize: 17, transition: 'transform 0.18s ease' }}
              />
            </Stack>
          </Box>
        ))}

        <Box
          component={RouterLink}
          to="/support"
          sx={{
            minHeight: 150,
            p: 2,
            borderRadius: 2,
            border: '1px solid rgba(35,154,164,0.16)',
            bgcolor: 'rgba(35,154,164,0.07)',
            textDecoration: 'none',
            color: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            '&:hover': {
              borderColor: '#239AA4',
              bgcolor: 'rgba(35,154,164,0.10)',
            },
          }}
        >
          <SupportAgentRoundedIcon sx={{ color: '#239AA4' }} />
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.75 }}>
              Need more help?
            </Typography>
            <Typography sx={{ color: '#64748B', fontSize: 12.5 }}>
              Raise a support request from inside AmbuNet when the docs do not
              cover your question.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
